import React, {
  useEffect, useState, memo, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import NumberFormat from 'react-number-format';
import _ from 'lodash';
import { useAnalytics } from 'use-analytics';
import clsx from 'clsx';
import withSpinner from '../../../HOCs/withSpinner';
import getCruisePriceBreakdownTotals from '../../../helpers/getCruisePriceBreakdownTotals';
import getPriceBreakdownKey from '../../../helpers/getPriceBreakdownKey';
import { checkDoShowAgentCommission } from '../../../store/slices/user';
import formatPriceRequest from '../../../helpers/formatPriceRequest';
import StateroomPrice from '../../TripConfiguration/StateroomPrice';
import AgentCommission from '../AgentCommission';

const PriceBreakdown = ({
  payload,
  doShowAgentCommission,
  stateroomNum,
  mode,
}) => {
  const priceBreakdowns = useSelector((state) => state.cruise.priceBreakdowns);
  const { staterooms } = useSelector((state) => state.tripSearch.searchObj);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [totals, setTotals] = useState(getCruisePriceBreakdownTotals(null));
  const formatStatementLine = (line) => line.charAt(0) + line.toLowerCase().slice(1);
  const { track } = useAnalytics();
  const defaulDoShowAgentCommission = useSelector(checkDoShowAgentCommission);
  const { isViewingBrochure } = useSelector((state) => state.brochure);
  const { isViewingCompare } = useSelector((state) => state.compare);

  useEffect(() => {
    if (priceBreakdown) {
      setTotals(getCruisePriceBreakdownTotals(priceBreakdown));
    }
  }, [priceBreakdown]);

  useEffect(() => {
    const key = getPriceBreakdownKey(formatPriceRequest({
      ...payload,
    }));
    if (priceBreakdowns && priceBreakdowns[key]) {
      if (!defaulDoShowAgentCommission) {
        const pb = [
          ...priceBreakdowns[key],
        ];
        try {
          _.each(pb, (p, roomIndex) => {
            const individualPassengerStatement = [...p.individualPassengerStatement];
            pb[roomIndex].individualPassengerStatement.forEach((ps, index) => {
              const cruiseRate = ps.statementLines.find((s) => s.title === 'CRUISE RATE');
              const promotions = ps.statementLines.filter((s) => s.title.indexOf('PROMOTION') > -1 && s.type === 'BASE RATE');
              const totalAmount = ps.statementLines.find((s) => s.type === 'TOTAL AMOUNT');
              const creditAmount = ps.statementLines.find((s) => s.type === 'CREDIT');
              let taxesAndFees = ps.statementLines.find((s) => s.title === 'TAXES AND FEES');
              let additionalCost = 0;
              ps.statementLines.forEach((s) => {
                if (s !== cruiseRate && s !== totalAmount && s !== taxesAndFees
                  && s !== creditAmount
                  && promotions.indexOf(s) === -1) {
                  additionalCost += +s.amount;
                }
              });
              if (!taxesAndFees) {
                taxesAndFees = {
                  amount: 0,
                  title: 'TAXES AND FEES',
                  type: 'TAXES AND FEES',
                };
              }
              taxesAndFees = {
                ...taxesAndFees,
                amount: +taxesAndFees.amount + additionalCost,
              };
              individualPassengerStatement[index] = {
                ...individualPassengerStatement[index],
                statementLines: _.filter([
                  cruiseRate, ...promotions, taxesAndFees, creditAmount, totalAmount,
                ], (f) => f !== undefined),
              };
            });
            pb[+roomIndex] = {
              ...pb[roomIndex],
              individualPassengerStatement: [...individualPassengerStatement],
            };
          });
        } catch (e) {
          console.error(e);
        }
        setPriceBreakdown(pb);
      } else {
        setPriceBreakdown(priceBreakdowns[key]);
      }
    }
  }, [priceBreakdowns, payload, staterooms, isViewingBrochure, defaulDoShowAgentCommission,
    stateroomNum, isViewingCompare]);

  useEffect(() => {
    track('priceBreakdown.onViewPriceBreakdown');
  }, [track]);

  const getTotalPrice = () => {
    const stateroomAmount = totals.paymentSchedules[stateroomNum]
      && totals.paymentSchedules[stateroomNum].finalPayment
      && totals.paymentSchedules[stateroomNum].finalPayment.amount;
    return stateroomNum !== undefined && stateroomAmount > 0 ? stateroomAmount : totals.totalAmount;
  };

  const getFilteredBreakdowns = (breakdown) => breakdown.filter((item, i) => {
    if (stateroomNum !== undefined) {
      return stateroomNum === i;
    }
    return true;
  });

  const renderComponent = () => (
    <div>
      {priceBreakdown && (
        <>
          <div className="px-5 py-4">
            <div className="font-bold text-lg">Price Breakdown:</div>
            {getFilteredBreakdowns(priceBreakdown)
              .map((bd, index) => (
                <Fragment key={`${index.toString()}`}>
                  {!bd.errors ? (
                    <>
                      {priceBreakdown.length > 1 && (
                        <div className={clsx({ 'mt-5': index > 0 }, 'font-bold')}>
                          Stateroom
                          {' '}
                          {stateroomNum ? stateroomNum + 1 : index + 1}
                        </div>
                      )}
                      {bd.individualPassengerStatement
                        && bd.individualPassengerStatement.map((passenger) => (
                          <div key={passenger.passengerNumber}>
                            <div className="border-b-2 mt-2 col-span-2">
                              Passenger
                              {' '}
                              {passenger.passengerNumber}
                            </div>
                            {passenger.statementLines.map((line) => (
                              <div key={line.title} className="grid grid-cols-3 pl-2">
                                <div className="mt-2 col-span-2">{formatStatementLine(line.title)}</div>
                                <div className="mt-2 text-right">
                                  <NumberFormat value={line.amount} prefix="$" displayType="text" thousandSeparator fixedDecimalScale="true" decimalScale="2" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                    </>
                  ) : ''}
                </Fragment>
              ))}
          </div>
          <div className="bg-body-0 px-5 pt-2 pb-4 border-t-2">
            {totals && (totals.errors || []).length === 0
              ? (
                <>
                  {totals
                    && (
                      <div className="mt-2 rounded-lg font-bold grid grid-cols-3">
                        <div className="col-span-2">Total</div>
                        <div className="text-right">
                          <NumberFormat
                            value={getTotalPrice()}
                            prefix="$"
                            displayType="text"
                            thousandSeparator
                            fixedDecimalScale="true"
                            decimalScale="2"
                          />
                        </div>

                      </div>
                    )}
                  {getFilteredBreakdowns(totals.paymentSchedules)
                    .map((paymentSchedule) => (
                      <StateroomPrice
                        key={paymentSchedule.roomIndex}
                        priceInfo={paymentSchedule}
                        doShowStateroomLabel={priceBreakdown.length > 1}
                      />
                    ))}

                  {totals.commission && doShowAgentCommission
                    && (
                      <AgentCommission
                        agentCommission={totals.commission.amount}
                        commissionPercentage={totals.commission.percent}
                        mode={mode}
                      />
                    )}
                </>
              )
              : (
                <div>
                  <div className="text-center font-bold">
                    An error has ocurred while loading rates:
                  </div>
                  <ul className="list-disc list-outside w-11/12 m-auto">
                    {totals.errors.map((item) => (
                      <li key={item.code}>
                        {item.description}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="text-sm text-left leading-5">
      {withSpinner(renderComponent, !priceBreakdown)}
    </div>
  );
};

PriceBreakdown.propTypes = {
  payload: PropTypes.shape({
    itineraryId: PropTypes.string,
    cabinCategoryCode: PropTypes.string,
    rateOptionCode: PropTypes.string,
    cabinNumber: PropTypes.string,
  }).isRequired,
  doShowAgentCommission: PropTypes.bool,
  stateroomNum: PropTypes.number,
  mode: PropTypes.string,
};

PriceBreakdown.defaultProps = {
  doShowAgentCommission: true,
  stateroomNum: undefined,
  mode: undefined,
};

export default memo(PriceBreakdown);
